import PropTypes from 'prop-types';
import React from 'react';

import {
  ARROW_POSITION_LEFT,
  ARROW_POSITION_RIGHT,
  arrowPositionList,
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
  colorList,
  colorSchemaList,
  ComponentSize,
  FontSize,
  fontSizeList,
  FontWeight,
  fontWeightList,
  IconPosition,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { getMappedColor, IClassNames, makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { iconSizeList, IIcon, IIconCustomSize, VerticalAlign, verticalAlignList } from '../icons/icon';
import IconArrowLink from '../icons/icon-arrow-link';
import Text from '../text/text';

import './cta.scss';

export interface ICta extends IComponent, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  href?: string;
  Icon?: React.FC<IIcon>;
  iconPosition?: IconPosition;
  iconSize?: ComponentSize;
  isMobileButtonWhite?: boolean;
  mobileButton?: boolean;
  hasWrapper?: boolean;
  uppercase?: boolean;
  narrow?: boolean;
  inline?: boolean;
  target?: string;
  verticalAlign?: VerticalAlign;
  iconCustomSize?: IIconCustomSize;
}

// To be used as DEFAULTS from CALLING COMPONENTS
export const COLOR_WITH_SCHEMA_DARK: Color = 'light';
export const COLOR_WITH_SCHEMA_LIGHT: Color = 'red-light';
export const DEFAULT_COLOR_MOBILE_WHITE: Color = 'light';
export const DEFAULT_COLOR: Color = 'red-light';
export const DEFAULT_FONT_SIZE: FontSize = '14';
export const DEFAULT_FONT_WEIGHT: FontWeight = '500';
export const DEFAULT_ICON_ALIGNMENT: VerticalAlign = 'middle';
export const DEFAULT_ICON_SIZE: ComponentSize = 'xsmall';

const colorSchemaMapping = {
  [COLOR_SCHEMA_DARK]: COLOR_WITH_SCHEMA_DARK,
  [COLOR_SCHEMA_DARK_LIGHT]: COLOR_WITH_SCHEMA_DARK,
  [COLOR_SCHEMA_LIGHT]: COLOR_WITH_SCHEMA_LIGHT,
};

const hasLeft = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_LEFT;
const hasRight = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_RIGHT;

const classNames = ({
  mobileButton,
  isMobileButtonWhite,
  uppercase = true,
  narrow = false,
  inline = false,
}: IClassNames): IClassNames =>
  makeClassNames({
    cta: [
      'cta',
      { 'cta--mobile-button': mobileButton },
      { 'cta--mobile-button-white': isMobileButtonWhite },
      { 'cta--uppercase': uppercase },
      { 'cta--expanded': !narrow },
      { 'cta--inline': narrow },
    ],
    prepend: 'cta__prepend',
    append: 'cta__append',
  });

const Cta: React.FC<ICta> = ({
  /* istanbul ignore next: default value */ color = DEFAULT_COLOR,
  /* istanbul ignore next: default value */ fontSize = DEFAULT_FONT_SIZE,
  /* istanbul ignore next: default value */ fontWeight = DEFAULT_FONT_WEIGHT,
  /* istanbul ignore next: default value */ hasWrapper = true,
  /* istanbul ignore next: default value */ href = '',
  /* istanbul ignore next: default value */ Icon = IconArrowLink,
  /* istanbul ignore next: default value */ iconSize = DEFAULT_ICON_SIZE,
  /* istanbul ignore next: default value */ isMobileButtonWhite = false,
  /* istanbul ignore next: default value */ mobileButton = false,
  /* istanbul ignore next: default value */ trackingData = {},
  /* istanbul ignore next: default value */ verticalAlign = DEFAULT_ICON_ALIGNMENT,
  /* istanbul ignore next: default value */ uppercase = true,
  /* istanbul ignore next: default value */ narrow = false,
  /* istanbul ignore next: default value */ inline = false,
  children,
  className,
  colorSchema,
  iconPosition,
  onClick,
  target,
  iconCustomSize,
}) => {
  // WARNING: colorSchema if setted always win on prop color
  const colorToUse: Color | undefined = isMobileButtonWhite
    ? DEFAULT_COLOR_MOBILE_WHITE
    : getMappedColor({ colorSchema, colorSchemaMapping, defaultColor: color });
  const cn = classNames({ mobileButton, isMobileButtonWhite, uppercase, narrow, inline });

  const arrowLeft: React.JSX.Element | null =
    iconPosition && hasLeft(iconPosition) ? (
      <span className={cn.prepend}>
        <Icon customSize={iconCustomSize} size={iconSize} color={colorToUse} reverse verticalAlign={verticalAlign} />
      </span>
    ) : null;

  const arrowRight: React.JSX.Element | null =
    iconPosition && hasRight(iconPosition) ? (
      <span className={cn.append}>
        <Icon customSize={iconCustomSize} size={iconSize} color={colorToUse} verticalAlign={verticalAlign} />
      </span>
    ) : null;

  const ctaText: React.JSX.Element = hasWrapper ? (
    <Text fontWeight={fontWeight} fontSize={fontSize} color={colorToUse}>
      {children}
    </Text>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );

  return (
    <a
      href={href}
      onClick={onClick}
      className={[cn.cta, className].filter(Boolean).join(' ')}
      {...objectToDataAttributes(trackingData)}
      target={target}
    >
      {arrowLeft}
      {ctaText}
      {arrowRight}
    </a>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  Cta.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    color: PropTypes.oneOf(colorList),
    colorSchema: PropTypes.oneOf(colorSchemaList),
    fontSize: PropTypes.oneOf(fontSizeList),
    fontWeight: PropTypes.oneOf(fontWeightList),
    hasWrapper: PropTypes.bool,
    href: PropTypes.string,
    Icon: PropTypes.func,
    iconPosition: PropTypes.oneOf(arrowPositionList),
    iconSize: PropTypes.oneOf(iconSizeList),
    isMobileButtonWhite: PropTypes.bool,
    mobileButton: PropTypes.bool,
    onClick: PropTypes.func,
    target: PropTypes.string,
    trackingData: PropTypes.object,
    verticalAlign: PropTypes.oneOf(verticalAlignList),
  };
}

export default Cta;
